import { useState, useEffect, useRef, useCallback } from "react";

const journalismStatuses = [
  "Transcribing whispers",
  "Decoding mumbles",
  "Chasing the lede",
  "Burying the lede",
  "Unburying the lede",
  "Fact-checking vibes",
  "Interviewing electrons",
  "Cultivating sources",
  "Decoding the discourse",
  "Going off the record",
  "Verifying rumors",
  "Spinning narratives",
  "Killing darlings",
  "Meeting deadlines",
  "Missing deadlines",
  "Attending press conferences",
  "Skipping press conferences",
  "Crafting headlines",
  "Busting myths",
  "Connecting dots",
  "Following the money",
  "Protecting sources",
  "Burning sources",
  "Reading between lines",
  "Typing furiously",
  "Staring at blank page",
  "Caffeinating journalists",
  "Decaffeinating editors",
  "Wrestling with quotes",
  "Attributing anonymous tips",
  "Spiking stories",
  "Unspiking stories",
  "Consulting stylebooks",
  "Ignoring stylebooks",
  "Counting column inches",
  "Trimming for space",
  "Adding for fluff",
  "Interviewing the microphone",
  "Questioning everything",
  "Questioning nothing",
  "Seeking comment",
  "Declining to comment",
  "Reaching for comment",
  "Unavailable for comment",
  "Parsing press releases",
  "Translating jargon",
  "Simplifying complexity",
  "Complicating simplicity",
  "Embedding with bytes",
  "Going gonzo",
  "Channeling Hemingway",
  "Invoking Murrow",
  "Summoning Woodward",
  "Consulting Bernstein",
  "Watergate-ing",
  "Pentagon Papers-ing",
  "Scoop-hunting",
  "Exclusive-fishing",
  "Breaking news-ing",
  "Developing story-ing",
  "Updating continuously",
  "Refreshing sources",
  "Cross-referencing facts",
  "Double-checking spellings",
  "Triple-checking names",
  "Quadruple-checking titles",
  "Proofreading proofs",
  "Editing editors",
  "Copyediting copy",
  "Rewriting rewrites",
  "Drafting drafts",
  "Finalizing finals",
  "Submitting submissions",
  "Publishing publications",
  "Broadcasting broadcasts",
  "Streaming streams",
  "Podcasting pods",
  "Blogging blogs",
  "Vlogging vlogs",
  "Tweeting tweets",
  "Threading threads",
  "Hashtagging tags",
  "Viral-izing content",
  "Engaging audiences",
  "Disengaging trolls",
  "Moderating comments",
  "Deleting spam",
  "Archiving archives",
  "Indexing indexes",
  "Cataloging catalogs",
  "Filing files",
  "Sorting soundbites",
  "Clipping clips",
  "Splicing splices",
  "Mixing mixes",
  "Mastering masters",
  "Rendering renders",
  "Encoding encodes",
  "Decoding decodes",
  "Compressing compressions",
];

const symbols = [
  "✶", "✦", "✧", "★", "☆", "✴", "✳", "❋", "✺", "✹",
  "†", "‡", "§", "¶", "※", "⁂", "⁕", "⁜", "⁑", "∞",
  "◈", "◆", "◇", "▲", "▼", "◉", "◎", "●", "○", "■",
  "♠", "♣", "♥", "♦", "♤", "♧", "♡", "♢", "⚔", "☽",
  "☾", "☿", "♁", "⚶", "⚷", "✞", "☠", "⚛", "⚡", "☀",
];

const randomSymbol = () => symbols[Math.floor(Math.random() * symbols.length)]!;
const randomPhrase = () => journalismStatuses[Math.floor(Math.random() * journalismStatuses.length)]! + "...";

const TYPE_SPEED = 40;
const ERASE_SPEED = 25;
const PAUSE_AFTER_TYPE = 1950;
const PAUSE_AFTER_ERASE = 300;

export function FunnyLoader() {
  const phrase0 = useRef(randomPhrase());
  const startIdx = useRef(Math.floor(Math.random() * phrase0.current.length));
  const [displayed, setDisplayed] = useState(phrase0.current.slice(0, startIdx.current));
  const phraseRef = useRef(phrase0.current);
  const indexRef = useRef(startIdx.current);
  const typingRef = useRef(true);
  const [cursorVisible, setCursorVisible] = useState(true);

  const tick = useCallback(() => {
    if (typingRef.current) {
      const phrase = phraseRef.current;
      indexRef.current++;
      setDisplayed(phrase.slice(0, indexRef.current));

      if (indexRef.current >= phrase.length) {
        typingRef.current = false;
        return PAUSE_AFTER_TYPE;
      }
      return TYPE_SPEED;
    } else {
      indexRef.current--;
      setDisplayed(phraseRef.current.slice(0, indexRef.current));

      if (indexRef.current <= 0) {
        phraseRef.current = randomPhrase();
        typingRef.current = true;
        return PAUSE_AFTER_ERASE;
      }
      return ERASE_SPEED;
    }
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const delay = tick();
      timeout = setTimeout(schedule, delay);
    };
    timeout = setTimeout(schedule, Math.random() * 1500);
    return () => clearTimeout(timeout);
  }, [tick]);

  useEffect(() => {
    const interval = setInterval(() => setCursorVisible((v) => !v), 530);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="text-muted-foreground">
      {displayed}
      <span className={cursorVisible ? "opacity-100" : "opacity-0"}>|</span>
    </span>
  );
}

export function FunnyBadge({ label }: { label: string }) {
  const [left, setLeft] = useState(randomSymbol());
  const [right, setRight] = useState(randomSymbol());

  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() < 0.5) {
        setLeft(randomSymbol());
      } else {
        setRight(randomSymbol());
      }
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="inline-flex items-center justify-center w-[6rem]">
      <span className="w-4 text-center">{left}</span> {label} <span className="w-4 text-center">{right}</span>
    </span>
  );
}
